import { faEyeSlash, faMousePointer } from '@fortawesome/free-solid-svg-icons';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
} from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as IsolationIcon } from '../../assets/icons/isolation.svg';

import {
  isShapeHidden,
  shapeDbIdDataAttribute,
  shapeUrnDataAttribute,
} from '../../components/SheetShapes';
import {
  DbIdsPerModel,
  SheetShapesMap,
} from '../../components/common/ForgeViewer';
import SparkelIcon from '../../components/common/icon/SparkelIcon';
import { SheetsVisibilityManager } from '../../components/viewers/common/hooks/useSheetsVisibilityManager';
import {
  ContextMenuContextType,
  ContextMenuProvider,
  OnShowProps,
  PredicateParams,
  useCustomContextMenu,
} from './CustomContextMenu';

export type SheetViewerMenuProps = {
  selections: DbIdsPerModel;
  hasSelectedElements: boolean;
  target?: {
    type: 'SHAPE';
    modelUrn: string;
    dbId: number;
  };
};

export const SheetContextMenuContext = createContext<
  ContextMenuContextType<SheetViewerMenuProps>
>({
  registerItem: () => {
    throw new Error('No Context Menu Context provider found.');
  },
  unregisterItem: () => {
    throw new Error('No Context Menu Context provider found.');
  },
  items: [],
});

export const SheetContextMenuProvider = ({
  children,
}: {
  children: React.ReactElement;
}) => {
  return (
    <ContextMenuProvider context={SheetContextMenuContext}>
      {children}
    </ContextMenuProvider>
  );
};

// Get mouse coordinates from event (returns null for keyboard events)
function getEventCoordinates(
  event:
    | React.MouseEvent<HTMLDivElement>
    | React.TouchEvent<HTMLDivElement>
    | React.KeyboardEvent<HTMLDivElement>
): { clientX: number; clientY: number } | null {
  if (event.type === 'mouseup') {
    const mouseEvent = event as React.MouseEvent<HTMLDivElement>;
    return { clientX: mouseEvent.clientX, clientY: mouseEvent.clientY };
  }
  if (event.type === 'touchstart') {
    const touch = (event as React.TouchEvent<HTMLDivElement>).touches[0];
    return { clientX: touch.clientX, clientY: touch.clientY };
  }
  return null;
}

// Find shape at cursor position using DOM attributes
function findShapeAtCursorDOM(
  coords: { clientX: number; clientY: number } | null
): { dbId: number; urn: string } | null {
  if (!coords) return null;

  const clickedElement = document.elementFromPoint(
    coords.clientX,
    coords.clientY
  );
  const dbIdString = clickedElement?.getAttribute(shapeDbIdDataAttribute);
  const urn = clickedElement?.getAttribute(shapeUrnDataAttribute);

  if (dbIdString && urn) {
    return { dbId: Number(dbIdString), urn };
  }
  return null;
}

export function useSheetViewerContextMenu(
  viewerRef: React.RefObject<HTMLDivElement>,
  visibilityManager: SheetsVisibilityManager,
  selectedDbIds: DbIdsPerModel,
  setSelectedDbIds: (dbIds: DbIdsPerModel) => void,
  renderedSheetShapes: SheetShapesMap
) {
  const showAndSelect = useCallback(
    async ({ event, showContextMenu }: OnShowProps<SheetViewerMenuProps>) => {
      if (!viewerRef.current) {
        return;
      }

      const coords = getEventCoordinates(event);
      const currentSelection = Object.values(selectedDbIds).flat();
      const clickedShape = findShapeAtCursorDOM(coords);

      const isClickedShapeInSelection =
        clickedShape &&
        selectedDbIds[clickedShape.urn]?.includes(clickedShape.dbId);

      if (clickedShape && !isClickedShapeInSelection) {
        setSelectedDbIds({ [clickedShape.urn]: [clickedShape.dbId] });
        showContextMenu({
          event,
          props: {
            selections: { [clickedShape.urn]: [clickedShape.dbId] },
            hasSelectedElements: true,
            target: {
              type: 'SHAPE',
              dbId: clickedShape.dbId,
              modelUrn: clickedShape.urn,
            },
          },
        });
      } else if (currentSelection.length === 1) {
        showContextMenu({
          event,
          props: {
            hasSelectedElements: true,
            selections: selectedDbIds,
            target: {
              type: 'SHAPE',
              dbId: currentSelection[0],
              modelUrn: Object.keys(selectedDbIds)[0],
            },
          },
        });
      } else if (currentSelection.length > 1) {
        showContextMenu({
          event,
          props: { hasSelectedElements: true, selections: selectedDbIds },
        });
      } else {
        showContextMenu({ event });
      }
    },
    [selectedDbIds, setSelectedDbIds, viewerRef]
  );

  const { registerItem, unregisterItem } = useSheetContextMenuContext();

  const selectAllVisibleHandler = useCallback(() => {
    const visibleShapes = Object.fromEntries(
      Object.keys(renderedSheetShapes).map((urn) => [
        urn,
        renderedSheetShapes[urn]
          .filter(
            (shape) =>
              !isShapeHidden(
                visibilityManager.hiddenElements,
                urn,
                shape,
                visibilityManager.hiddenSheetShapes,
                visibilityManager.isolatedElements,
                false
              )
          )
          .map((shape) => shape.dbId),
      ])
    );
    setSelectedDbIds(visibleShapes);
  }, [
    renderedSheetShapes,
    setSelectedDbIds,
    visibilityManager.hiddenElements,
    visibilityManager.hiddenSheetShapes,
    visibilityManager.isolatedElements,
  ]);

  const hideSelectedHandler = useCallback(() => {
    visibilityManager.hide(selectedDbIds);
    setSelectedDbIds({});
  }, [selectedDbIds, setSelectedDbIds, visibilityManager]);

  const isolateSelectedHandler = useCallback(() => {
    visibilityManager.setIsolatedElements(selectedDbIds);
  }, [selectedDbIds, visibilityManager]);

  const hideSelectionRelatedItems = useCallback(
    ({ props }: PredicateParams<SheetViewerMenuProps>) => {
      if (!props) {
        return true;
      } else {
        return !props.hasSelectedElements;
      }
    },
    []
  );

  const { t } = useTranslation('project');

  useEffect(() => {
    registerItem({
      id: 'isolateSelected',
      label: t('viewer-menu.isolate-selected'),
      onClick: isolateSelectedHandler,
      hidden: hideSelectionRelatedItems,
      group: 'viewer visibility',
      icon: <SparkelIcon as={IsolationIcon} />,
    });
    return () => {
      unregisterItem('isolateSelected');
    };
  }, [
    hideSelectionRelatedItems,
    registerItem,
    isolateSelectedHandler,
    unregisterItem,
    t,
  ]);

  useEffect(() => {
    registerItem({
      id: 'hideSelected',
      label: t('viewer-menu.hide-selected'),
      onClick: hideSelectedHandler,
      hidden: hideSelectionRelatedItems,
      group: 'viewer visibility',
      icon: <SparkelIcon size="sm" fixedWidth icon={faEyeSlash} />,
    });
    return () => {
      unregisterItem('hideSelected');
    };
  }, [
    hideSelectionRelatedItems,
    registerItem,
    hideSelectedHandler,
    unregisterItem,
    t,
  ]);

  useEffect(() => {
    registerItem({
      id: 'selectAllVisible',
      label: t('viewer-menu.select-visible'),
      onClick: selectAllVisibleHandler,
      group: 'viewer selection',
      icon: <SparkelIcon size="sm" fixedWidth icon={faMousePointer} />,
    });
    return () => {
      unregisterItem('selectAllVisible');
    };
  }, [registerItem, selectAllVisibleHandler, t, unregisterItem]);

  const { items } = useSheetContextMenuContext();
  return useCustomContextMenu(showAndSelect, items, [
    'viewer visibility',
    'viewer selection',
  ]);
}
export const useSheetContextMenuContext = () =>
  useContext(SheetContextMenuContext);
